import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: any[]) {
  return twMerge(clsx(inputs));
}

/**
 * Logs a message to the console with the specified color.
 */
export const logColor = (colorName: string, message: string, value?: any) => {
  const colors: Record<string, string> = {
    "pink": "#D84999",
    "cyan": "#00DDFF",
    "yellow": "#F7D32A",
    "green": "#4ADE80",
    "red": "#EF4444",
    "blue": "#3B82F6",
    "purple": "#A855F7",
    "orange": "#FB923C"
  };

  const color = colors[colorName] || "#000000";

  if (value !== undefined) {
    console.log(`%c${message}`, `color: ${color}; font-weight: bold;`, value);
  } else {
    console.log(`%c${message}`, `color: ${color}; font-weight: bold;`);
  }
};

/**
 * Checks if a date is valid
 */
export const isValidDate = (date: any): date is Date => {
  return date instanceof Date && !isNaN(date.getTime());
};

/**
 * Formats a date string
 */
export const formatDate = (date: string | Date, format: string = 'YYYY-MM-DD'): string => {
  if (!date) return '';

  const d = typeof date === 'string' ? new Date(date) : date;
  if (!isValidDate(d)) return '';

  // Simple date formatting - extend as needed
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');

  switch (format) {
    case 'DD.MM.YYYY':
      return `${day}.${month}.${year}`;
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    default:
      return `${year}-${month}-${day}`;
  }
};

/**
 * Debounce function
 */
export const debounce = <T extends (...args: any[]) => void>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * Throttle function
 */
export const throttle = <T extends (...args: any[]) => void>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let lastTime = 0;

  return (...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastTime >= wait) {
      lastTime = now;
      func(...args);
    }
  };
};

/**
 * Get default profile messages - simplified version without store
 */
export const getDefaultProfileMessages = () => {
  return {
    active: "Your call is being forwarded.",
    inactive: "Thank you for calling."
  };
};

/**
 * Simple string truncation
 */
export const truncateString = (str: string, maxLength: number): string => {
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength - 3) + '...';
};

/**
 * Deep clone object
 */
export const deepClone = <T>(obj: T): T => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime()) as T;
  if (obj instanceof Array) return obj.map(item => deepClone(item)) as T;
  if (typeof obj === 'object') {
    const clonedObj = {} as T;
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj;
  }
  return obj;
};

// Placeholder functions for call-routing-store
export const deleteEntryByIndex = (array: any[], index: number) => {
  return array.filter((_, i) => i !== index);
};

export const updateEntryAtIndex = (array: any[], index: number, newEntry: any) => {
  return array.map((item, i) => i === index ? newEntry : item);
};

// Toast placeholder functions - these should be replaced with proper toast implementation
export const showToastDeleteCallRoutingException = (message?: string) => {
  console.log('Toast: Delete call routing exception', message);
};

export const showToastDeleteCallRoutingObject = (message?: string) => {
  console.log('Toast: Delete call routing object', message);
};

export const showToastErrorModifyCallRoutingObject = (message?: string) => {
  console.log('Toast: Error modify call routing object', message);
};

export const showToastNewCallRoutingObject = (message?: string) => {
  console.log('Toast: New call routing object', message);
};

export const showToastUpdateCallRoutingExceptionTime = (message?: string) => {
  console.log('Toast: Update call routing exception time', message);
};

export const showToastUpdateCallRoutingObject = (message?: string) => {
  console.log('Toast: Update call routing object', message);
};

export const showToastUpdateGoodbyeMessage = (message?: string) => {
  console.log('Toast: Update goodbye message', message);
};

export const showToastUpdateGreetingMessage = (message?: string) => {
  console.log('Toast: Update greeting message', message);
};